/**
 * PlmQcTab — the "QC Result View" tab (prototype v8, `#pane-qc`).
 *
 * One row per QC re-read in the batch: what the original layer reported, what
 * the re-read reported, and the verdict. Clicking a row expands the four
 * checks behind that verdict, so an analyst sees WHY a pair failed rather than
 * just that it did.
 *
 * All rules live in {@link plmQc} — this file only renders them.
 */
import { useMemo, useState } from 'react';
import { LoaderSymbol } from '../../../components/loading';
import { fmtLocalDateTime } from '../../../utils/datetime';
import type { PlmCorrectiveAction, PlmQcCompareRow, PlmQcHistoryRow } from './plm.api';
import {
  POSITIVE_THRESHOLD_PCT, asbestosSummary, buildQcPairs, groupLayers, type QcPair,
} from './plmQc';
import { pairLimit, toPoints, type QcPoint } from './plmQcStats';
import './plm.css';

type Props = {
  /** Flat rows from `AI_Plm_QcCompare_SP`. */
  rows: readonly PlmQcCompareRow[];
  loading?: boolean;
  /** Highlights the pair that involves the layer currently open. */
  activeSequenceOid?: string | null;
  /** Opens a layer from the QC table. */
  onOpenLayer?: (sequenceOid: string) => void;
  /** The corrective actions recorded on the batch's dups (prototype v18). */
  corrective?: readonly PlmCorrectiveAction[];
  /** Saves a failed pair's corrective action; absent = read-only (e.g. validated). */
  onSaveCorrective?: (sequenceOid: string, text: string) => Promise<void>;
  /** The lab's re-reads of the last 3 months — the limits behind each pair's line. */
  history?: readonly QcPoint[] | null;
  /** Opens the QC chart of a dup kind ("open chart →"). */
  onOpenChart?: (kind: 'Intra' | 'Inter') => void;
};

const STATE_LABEL: Record<QcPair['state'], string> = {
  pass: 'Pass', fail: 'Fail', pending: 'Pending',
};

/** The SIMPLE dup flow (2026-10-17): a side of the pair is blind to this user (the
 *  server sent its identity only), so the pair shows no results and no verdict. */
const isBlindPair = (p: QcPair) => !!(p.reRead.blind || p.original?.blind);
const BLIND_LABEL = '🔒 Blind';

export function PlmQcTab({
  rows, loading, activeSequenceOid, onOpenLayer, corrective, onSaveCorrective, history, onOpenChart,
}: Props) {
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());

  const pairs = useMemo(() => buildQcPairs(groupLayers(rows)), [rows]);
  // Each pair as a chart point (its error), to place it against the lab's limits.
  const points = useMemo(
    () => new Map(toPoints(rows as readonly PlmQcHistoryRow[]).map(p => [p.sequenceOid, p])), [rows]);
  const actionOf = useMemo(
    () => new Map((corrective ?? []).map(a => [a.sequenceOid.toLowerCase(), a])), [corrective]);

  const counts = useMemo(() => {
    const seen = pairs.filter(p => !isBlindPair(p));
    return {
      pass: seen.filter(p => p.state === 'pass').length,
      fail: seen.filter(p => p.state === 'fail').length,
      pending: seen.filter(p => p.state === 'pending').length,
      blind: pairs.length - seen.length,
    };
  }, [pairs]);

  const toggle = (oid: string) => setExpanded(prev => {
    const next = new Set(prev);
    if (!next.delete(oid)) next.add(oid);
    return next;
  });

  if (loading) {
    return (
      <div className="plm-qc__loading">
        <LoaderSymbol />
        <span>Reading the batch…</span>
      </div>
    );
  }

  return (
    <div className="plm-qc">
      <div className="plm-qc__sum">
        <b className="plm-qc__n">{pairs.length}</b>
        {' '}QC entr{pairs.length === 1 ? 'y' : 'ies'}
        {' · '}{counts.pass} pass · {counts.fail} fail · {counts.pending} pending
        {counts.blind > 0 && <>{' · '}{counts.blind} blind</>}
      </div>
      <div className="plm-qc__crit">
        Pass = same classification (&gt; {POSITIVE_THRESHOLD_PCT}%), same asbestos
        types, and the re-read inside the EPA 600 acceptable range · Intra = the
        same analyst re-reading their own slide · Inter = a second analyst.
      </div>

      {pairs.length === 0 ? (
        <div className="plm-qc__empty">
          No QC re-reads in this batch. The Analysis Queue adds them to the
          sequence as Intra-dup or Inter-dup layers.
        </div>
      ) : (
        <div className="plm-qc__tablewrap">
          <table className="plm-qc__table">
            <thead>
              <tr>
                <th className="plm-qc__caret" aria-label="Expand" />
                <th>Layer</th><th>QC</th>
                <th>Original / Reference</th><th>QC read</th><th>Result</th>
              </tr>
            </thead>
            <tbody>
              {pairs.map(p => {
                const key = p.reRead.sequenceOid;
                const blind = isBlindPair(p);
                const open = !blind && expanded.has(key) && p.checks.length > 0;
                const isCurrent = activeSequenceOid === key
                  || activeSequenceOid === p.original?.sequenceOid;
                return [
                  <tr
                    key={key}
                    className={`plm-qc__row${isCurrent ? ' is-current' : ''}`}
                    onClick={() => { if (!blind) toggle(key); }}
                    aria-expanded={!blind && p.checks.length ? open : undefined}
                  >
                    <td className="plm-qc__caret">{!blind && p.checks.length ? (open ? '▾' : '▸') : ''}</td>
                    <td className="plm-qc__id">
                      {onOpenLayer && p.original ? (
                        <button
                          type="button"
                          className="plm-qc__link"
                          onClick={e => { e.stopPropagation(); onOpenLayer(p.original!.sequenceOid); }}
                          title="Open the original layer"
                        >{p.original.sampleLayer ?? '—'}</button>
                      ) : (p.original?.sampleLayer ?? p.reRead.sampleLayer ?? '—')}
                    </td>
                    <td className="plm-qc__qccell">
                      <span className={`plm-qc__type plm-qc__type--${p.kind.toLowerCase()}`}>
                        {p.kind.toUpperCase()}
                      </span>
                      <span className="plm-qc__analyst">{p.reRead.blind ? '' : (p.reRead.analyzedByName || '—')}</span>
                    </td>
                    <td>{p.original?.blind ? BLIND_LABEL : p.original ? asbestosSummary(p.original) : '—'}</td>
                    <td>
                      {p.reRead.blind ? BLIND_LABEL
                        : p.state === 'pending'
                        ? <span className="plm-qc__muted">not entered</span>
                        : asbestosSummary(p.reRead)}
                    </td>
                    <td>
                      {blind ? (
                        <span className="plm-qc__pill plm-qc__pill--pending" data-testid="plm-qc-blind"
                          title="Hidden until the batch is submitted — only the original analyst sees it">
                          {BLIND_LABEL}
                        </span>
                      ) : (
                      <span className={`plm-qc__pill plm-qc__pill--${p.state}`}>
                        {STATE_LABEL[p.state]}
                      </span>
                      )}
                      {!blind && p.state === 'fail' && !actionOf.has(key.toLowerCase()) && (
                        <span className="plm-qc__needs" title="Corrective action required before Validation">
                          {' '}corrective action needed
                        </span>
                      )}
                    </td>
                  </tr>,
                  open ? (
                    <tr key={`${key}-detail`} className="plm-qc__detail">
                      <td />
                      <td colSpan={5}>
                        <div className="plm-qc__checks">
                          {p.checks.map(c => (
                            <span
                              key={c.label}
                              className={`plm-qc__chk plm-qc__chk--${c.na ? 'na' : c.ok ? 'ok' : 'bad'}`}
                            >
                              <span className="plm-qc__chkic" aria-hidden="true">
                                {c.na ? '–' : c.ok ? '✓' : '✗'}
                              </span>
                              {c.label}
                            </span>
                          ))}
                        </div>
                        {p.rows.filter(r => r.state === 'bad').length > 0 && (
                          <div className="plm-qc__bad">
                            {p.rows.filter(r => r.state === 'bad').map(r => (
                              <div key={r.name}>
                                {r.name}: {r.original?.raw ?? '—'} vs {r.reRead?.raw ?? '—'}
                                {r.error != null
                                  ? ` (error ${r.error > 0 ? '+' : ''}${r.error}%)`
                                  : ''}
                              </div>
                            ))}
                          </div>
                        )}
                        {p.reason && <div className="plm-qc__bad">{p.reason}</div>}
                        <PairLimitLine point={points.get(key)} history={history} onOpenChart={onOpenChart} />
                        {p.state === 'fail' && (
                          <CorrectiveAction
                            sequenceOid={key}
                            saved={actionOf.get(key.toLowerCase())}
                            onSave={onSaveCorrective}
                          />
                        )}
                      </td>
                    </tr>
                  ) : null,
                ];
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/** The prototype's lab-limit line: this pair's error against the lab's own
 *  rolling limits for the same dup kind, with a link to the chart. */
function PairLimitLine({ point, history, onOpenChart }: {
  point: QcPoint | undefined;
  history: readonly QcPoint[] | null | undefined;
  onOpenChart?: (kind: 'Intra' | 'Inter') => void;
}) {
  if (!point || !history) return null;
  const limit = pairLimit(point, history);
  if (!limit) {
    return <div className="plm-qc__limit">Not enough {point.kind}-dups in the last 3 months for lab control limits.</div>;
  }
  return (
    <div className={`plm-qc__limit is-${limit.tone}`}>
      {limit.text}
      {onOpenChart && (
        <>
          {' · '}
          <button type="button" className="plm-qc__link"
            onClick={e => { e.stopPropagation(); onOpenChart(point.kind === 'Inter' ? 'Inter' : 'Intra'); }}>
            open chart →
          </button>
        </>
      )}
    </div>
  );
}

/** "Corrective action (required before Validation)" under a FAILED pair
 *  (prototype v18). Yellow until one is saved; the server refuses Validate
 *  while a failed pair has none. */
function CorrectiveAction({ sequenceOid, saved, onSave }: {
  sequenceOid: string;
  saved: PlmCorrectiveAction | undefined;
  onSave?: (sequenceOid: string, text: string) => Promise<void>;
}) {
  const [draft, setDraft] = useState(saved?.text ?? '');
  const [busy, setBusy] = useState(false);
  const changed = draft.trim() !== (saved?.text ?? '');
  return (
    <div className="plm-qc__ca" onClick={e => e.stopPropagation()}>
      <label className="plm-qc__ca-l" htmlFor={`ca-${sequenceOid}`}>
        Corrective action <span className="plm-qc__ca-req">(required before Validation)</span>
      </label>
      <div className="plm-qc__ca-row">
        <input
          id={`ca-${sequenceOid}`}
          className={`plm-input plm-qc__ca-in${saved?.text ? '' : ' is-required'}`}
          value={draft}
          maxLength={500}
          placeholder="e.g. Re-prepared slide, re-analysed"
          readOnly={!onSave}
          onChange={e => setDraft(e.target.value)}
        />
        {onSave && (
          <button type="button" className="ca-btn ca-btn--sm" disabled={busy || !changed}
            onClick={async () => {
              setBusy(true);
              try { await onSave(sequenceOid, draft.trim()); } finally { setBusy(false); }
            }}>Save</button>
        )}
      </div>
      {saved?.text && (
        <div className="plm-qc__ca-stamp">
          Recorded by {saved.byName ?? '—'}{saved.at ? ` · ${fmtLocalDateTime(saved.at)}` : ''}
        </div>
      )}
    </div>
  );
}
